import React, {useEffect, useState} from "react";
import {
	Alert, Avatar, Button, Card,
	CardContent, CardHeader,
	Chip, Divider, FormControlLabel,
	IconButton,
	Input, Radio, RadioGroup,
	Stack, Switch,
	Tooltip,
	Typography,
} from "@mui/material";
import {DocumentMeta} from "../../models/DocumentMeta";
import {DocumentValidation} from "../../models/DocumentValidation";
import {
	Delete,
	ErrorOutline,
	Info,
	LocalHospital,
	WarningOutlined,
	AttachFile, ContentPaste
} from "@mui/icons-material";
import {ValidationSeverity} from "../../models/ValidationSeverity";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {CustomerLookup} from "./CustomerLookup";
import {PolicyLookup} from "./PolicyLookup";
import gqlOnChangeMeta from "../graphql/mutations/gqlOnChangeMeta";
import {GenericModal} from "../../../querytool/components/generic-modal/GenericModal";
import {GridFilterItem} from "@mui/x-data-grid/models/gridFilterItem";
import {MetaSelector} from "./MetaSelector";
import {MetaValueType} from "../../models/MetaValueType";
import {ClaimLookup} from "./ClaimLookup";
import {translateFunction} from "../../../../localization/TranslationService";
import {MetaObject} from "../../models/MetaObject";
import {RefBesLookup} from "./RefBesLookup";
import {DocumentAudit} from "../../models/DocumentAudit";
import { ModalLoader } from "../../../../components/common/ModalLoader";
import {isAuthorizedByAnyGroup} from "../../../fragments/PageAuthorization";
import {Lookup} from "./Lookup";
import {GET_TARGETS} from "../graphql/queries/gqlGetTargets";
import {ITarget} from "../../models/Target";
import {DocumentFile} from "../../models/DocumentFile";
import {DocumentFileType} from "../../models/DocumentFileType";
import {blue} from "@mui/material/colors";


const translate = translateFunction("SureBaseDocuments");

// Metadata extracted from the hidden "Surebase: true" block in the mail body
const SUREBASE_HIDDEN_FIELD_PREFIX = "Surebase_";

interface IMetaCardProps {
	metaData: DocumentMeta[] | null;
	documentId: number;
	validations?: DocumentValidation[] | null;
	files?: DocumentFile[] | null;
	onMetaDataChanged?: (metaData: DocumentMeta[] | null) => void;
	onValidationsChanged?: (validationData: DocumentValidation[] | null) => void;
	onAuditsChanged?: (auditData: DocumentAudit[] | null) => void;
	readOnly?: boolean;
	hideFields?: string[];
	setCurrentFile?: (newFileId: number) => void;
	currentFile?: number;
}

enum lookupType {
	Customer,
	Policy,
	Category,
	Claim,
	TaskType,
	Segment,
	User,
	ClaimType,
	CedFileType = 8,
	ClaimCedConcerns,
	SurebaseFileType,
	SurebaseCategory
}
interface ILookupProps{
	type: lookupType;
	autoSelectSingleRecord?: boolean;
	filter?: GridFilterItem[];
	metaKeyPrefix?: string;
}

interface IFileType {
	fileType: string;
	fileTypeId: string;
	lookup: lookupType;
}

interface IFilesInputProps {
	metaKeyPrefix: string;
	fileTypeLookup: lookupType;
	attachmentExtraInput?: (file: DocumentFile) => React.ReactNode;
}
export const MetaCard: React.FC<IMetaCardProps> = (props) => {
	const [metaData, setMetaData] = useState<DocumentMeta[] | null>([...props.metaData??[]]);
	const [validations, setValidations] = useState<DocumentValidation[] | null>([...props.validations??[]]);
	const [loading, setLoading] = useState<boolean>(false);
	const [debug, setDebug] = useState<boolean>(false);
	const [metaUpdate, setMetaUpdate] = useState<string>();
	const [lookup, setLookup] = useState<ILookupProps>();
	const [targets, setTargets] = useState<ITarget[]>([]);
	const [copyFileType, setCopyFileType] = useState<IFileType>();
	
	const [metaObject, setMetaObject] = useState<MetaObject>(new MetaObject(metaData));
	
	
	const updateMetaData = (values: DocumentMeta[]) => {
		setMetaData(values);
		setMetaUpdate(new Date().getMilliseconds().toString());
		setMetaObject(new MetaObject(values));
		if(props.onMetaDataChanged) {
			props.onMetaDataChanged(values);
		}
	};

	const updateValidations = (values: DocumentValidation[]) => {

		setValidations(values);
		if (props.onValidationsChanged) {
			props.onValidationsChanged(values);
		}
	};

	const updateAudits = (values: DocumentAudit[]) => {
		if (props.onAuditsChanged){
			props.onAuditsChanged(values);
		}
	};
	
	useEffect(() => {
		setLoading(true);
		graphQLServices.documentsClient.query({
			query: GET_TARGETS
		}).then((result) => {
			setTargets(result?.data.targets);
		}).finally(() => {
			setLoading(false);
		});
	},[]);
	
	const TaskInput = () => {
		return (
			<Stack key={"taskRegion"}>
				<Divider textAlign={"left"} ><Typography variant="h6">Agenda</Typography></Divider>
				<MetaSelector
					key={"newTask" + metaUpdate}
					readOnly={props.readOnly}
					metaType = {MetaValueType.Boolean}
					label={translate("New task")}
					metaKey={"newTask"}
					search={false}
					metaData={metaData}
					validations={validations}
					onChange={async (key, field, value) => await updateField({key: key, value: value})}
				/>
				{metaObject.newTask &&
					<Stack direction={"column"}>
						<MetaSelector
							key={"taskUrgent" + metaUpdate}
							readOnly={props.readOnly}
							metaType = {MetaValueType.Boolean}
							label={translate("Urgent")}
							metaKey={"taskUrgent"}
							search={false}
							metaData={metaData}
							validations={validations}
							onChange={async (key, field, value) => {
								return await updateFields([{key: key, value: value}]);
							}}
						/>
						<MetaSelector
							key={"assignedUserId" + metaUpdate}
							readOnly={props.readOnly}
							field={"code"}
							label={"Behandelaar"}
							metaKey={"assignedUserId"}
							metaType={MetaValueType.String}
							search={true}
							metaData={metaData}
							validations={validations}
							onSearch={(field, value, searchClicked)=> {
								setLookup({type: lookupType.User,autoSelectSingleRecord: !searchClicked, filter: searchClicked?undefined:[{field: field??"", value: value, operator: "startsWith"}]});
							}}
						/>

						{!metaObject.taskUrgent &&
						<MetaSelector
							readOnly={props.readOnly}
							key={"taskType" + metaUpdate}
							field={"description"}
							label={translate("Type")}
							metaKey={"taskType"}
							metaType={MetaValueType.String}
							search={true}
							metaData={metaData}
							validations={validations}
							onSearch={(field, value, searchClicked)=> {
								setLookup({type: lookupType.TaskType,autoSelectSingleRecord: !searchClicked, filter: searchClicked?undefined:[{field: field??"", value: value, operator: "startsWith"}]});
							}}
							//onChange={async (key, field, value) => await updateField({key: key, value: value})}
						/>}
						<MetaSelector
							search={false}
							readOnly={props.readOnly}
							key={"taskSubject" + metaUpdate}
							label={translate("Subject")}
							metaKey={"taskSubject"}
							metaType={MetaValueType.String}
							metaData={metaData}
							validations={validations}
							onChange={async (key, field, value) => await updateField({key: key, value: value})}
						/>
					</Stack>
				}
			</Stack>
		);
	};
	
	
	const GeneralInput = () => {
		return (

			<Stack key={"general" + metaUpdate} >
				<Divider textAlign={"left"} ><Typography variant="h6">Document</Typography></Divider>
				<MetaSelector
					key="category"
					readOnly={props.readOnly}
					field={"description"}
					label={translate("Category")}
					metaKey={"documentCategory"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						setLookup({type: lookupType.Category,autoSelectSingleRecord: !searchClicked});
					}}
					onChange={async (key, field, value) => {
						setLookup({type: lookupType.Category,autoSelectSingleRecord: false, filter: [{field: field??"", value: value, operator: "startsWith"}]});
						//await updateField({key: key, value: value})
						return true;
					}}
				/>

				<MetaSelector
					key="documentSegment"
					readOnly={props.readOnly}
					field={"omschrijving"}
					label={translate("Segment")}
					metaKey={"documentSegment"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						setLookup({type: lookupType.Segment,autoSelectSingleRecord: !searchClicked, filter: searchClicked?undefined:[{field: field??"", value: value, operator: "startsWith"}]});
					}}
				/>

				<Divider textAlign={"left"} ><Typography variant="h6">Zichtbaar voor</Typography></Divider>
				<MetaSelector
					key={"visibleForBroker"}
					readOnly={props.readOnly}
					metaType = {MetaValueType.Boolean}
					label={translate("Broker")}
					metaKey={"visibleForBroker"}
					search={false}
					metaData={metaData}
					validations={validations}
					onChange={async (key, field, value) => await updateField({key: key, value: value})}
				/>
				<MetaSelector
					key={"visibleForCustomer"}
					readOnly={props.readOnly}
					metaType = {MetaValueType.Boolean}
					label={translate("Customer")}
					metaKey={"visibleForCustomer"}
					search={false}
					metaData={metaData}
					validations={validations}
					onChange={async (key, field, value) => await updateField({key: key, value: value})}
				/>
			</Stack>
		);

	};
	const CustomerInput = () => {
		return(

			<Stack key={"customer" + metaUpdate}>
				<Divider textAlign={"left"}>
					<Typography variant="h6">
						Relatie
						{metaObject.brokerName && <Tooltip title={translate("Broker:") + metaObject.brokerName}><IconButton><Info/></IconButton></Tooltip>}
						{!props.readOnly && <IconButton onClick={async () =>
							await updateFields([{key:"customerId", value:""}, {key:"customerName", value:""}, {key:"zipCode", value:""}])}><Delete/></IconButton>}
					</Typography>
				</Divider>
				<MetaSelector
					readOnly={props.readOnly}
					key={"customerId"}
					field={"customer.id"}
					label={"Nr"}
					metaKey={"customerId"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						// eslint-disable-next-line no-debugger
						setLookup({type: lookupType.Customer,autoSelectSingleRecord: !searchClicked, filter: [{field: field??"", value: value, operator: "startsWith"}]});
						//setlookupCustomers(true);
						//setlookupCustomersFilter([{field: field, value: value, operator: "startsWith"}]);
					}}
					onChange={async (key, field, value) => await updateField({key: key, value: value})}
				/>
				<MetaSelector
					readOnly={props.readOnly}
					key={"customerName"}
					field={"customer.naam"}
					label={"Naam"}
					metaKey={"customerName"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						setLookup({type: lookupType.Customer,autoSelectSingleRecord: !searchClicked, filter: [{field: field??"", value: value, operator: "startsWith"}]});
						//setlookupCustomers(true);
						//setlookupCustomersFilter([{field: field, value: value, operator: "startsWith"}]);
					}}
					onChange={async () => false}
					//					onChange={async (key, field, value) => {
					//						setlookupCustomers(true);
					//						setlookupCustomersFilter([{field: field??"", value: value, operator: "startsWith"}]);
					//						return true;
					//					}}
				/>
				<MetaSelector
					readOnly={props.readOnly}
					key={"zipCode"}
					field={"customer.postcode"}
					label={"Postcode"}
					metaKey={"zipCode"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						setLookup({type: lookupType.Customer,autoSelectSingleRecord: !searchClicked, filter: [{field: field??"", value: value, operator: "startsWith"}]});
						//						setlookupCustomers(true);
						//						setlookupCustomersFilter([{field: field, value: value, operator: "startsWith"}]);
					}}
					/*
					onChange={async (key, field, value) => {
						setlookupCustomers(true);
						setlookupCustomersFilter([{field: field ?? "", value: value, operator: "startsWith"}]);
						return true;
					}
					}
*/
				/>

			</Stack>
		);
	};

	const PolicyInput = () => {
		return(
			<Stack key={"policy" + metaUpdate}>
				<Divider textAlign={"left"}>
					<Typography variant="h6">
						Polis/Pakket
						{!props.readOnly && <IconButton onClick={async () =>
							await updateFields([{key:"policyId", value:""},{key:"policyNr", value:""}, {key:"licensePlate", value:""}])}><Delete/></IconButton>}
					</Typography>
				</Divider>
				{metaObject.policyIsPackage &&
				<Chip label="Pakket" />
				}
				<MetaSelector
					readOnly={props.readOnly}
					key="policyid"
					field={"policy.id"}
					label={"Id"}
					metaKey={"policyId"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						let filter = [{field: field, value: value, operator: "startsWith"}];
						const customerId = metaData?.filter((it)=> it.key === "customerId" ).first()?.value;
						if (customerId) {
							filter = [...filter, {field: "policy.relnr", value: customerId, operator: "equals"}];
						}setLookup({type: lookupType.Policy,autoSelectSingleRecord: !searchClicked, filter: filter});
					}}
					onChange={async (key, field, value) => await updateField({key: key, value: value})}
				/>
				<MetaSelector
					readOnly={props.readOnly}
					key="policy1"
					field={"policy.nr"}
					label={"Nr"}
					metaKey={"policyNr"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						let filter = [{field: field, value: value, operator: "startsWith"}];
						const customerId = metaData?.filter((it)=> it.key === "customerId" ).first()?.value;
						if (customerId) {
							filter = [...filter, {field: "policy.relnr", value: customerId, operator: "equals"}];
						}
						setLookup({type: lookupType.Policy,autoSelectSingleRecord: !searchClicked, filter: filter});
					}}
					onChange={async (key, field, value) => await updateField({key: key, value: value})}
				/>
				<MetaSelector
					readOnly={props.readOnly}
					key="policy2"
					field={"policy.kenteken"}
					label={"Kenteken"}
					metaKey={"licensePlate"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						let filter = [{field: field, value: value, operator: "startsWith"}];
						const customerId = metaData?.filter((it)=> it.key === "customerId" ).first()?.value;
						if (customerId) {
							filter = [...filter, {field: "policy.relnr", value: customerId, operator: "equals"}];
						}
						setLookup({type: lookupType.Policy,autoSelectSingleRecord: !searchClicked, filter: filter});
					}}
				/>
			</Stack>
		);
	};
	
	const FilesInput = (filesProps: IFilesInputProps) => {
		const bodyFile = props.files?.filter((it) => it.type === DocumentFileType.Body).first();
		const attachments = props.files?.filter((it) => it.type === DocumentFileType.Attachment);
		const pasteFileType = copyFileType?.lookup === filesProps.fileTypeLookup ? copyFileType : undefined;

		return (
			<>
				{bodyFile &&
					<Card>
						<CardHeader title={
							<Typography noWrap gutterBottom variant="h6" component="h4">
								Mail Body
							</Typography>
						} avatar={
							<Button variant={"outlined"} onClick={() => {
								props.setCurrentFile && props.setCurrentFile(bodyFile.id);
							}
							}>
								<Avatar sx={{ bgcolor: blue[(bodyFile.id ===props.currentFile) ? 500 : 50] }}>
									<AttachFile/>
								</Avatar>
							</Button>
						}/>
						<CardContent>
							<MetaSelector
								key={filesProps.metaKeyPrefix + bodyFile.id + ".Type"}
								field={"description"}
								label={translate("Type")}
								metaKey={filesProps.metaKeyPrefix + bodyFile.id + ".Type"}
								metaType={MetaValueType.String}
								search={true}
								metaData={metaData}
								validations={validations}
								onSearch={(field, value, searchClicked) => {
									setLookup({
										type: filesProps.fileTypeLookup,
										metaKeyPrefix: filesProps.metaKeyPrefix + bodyFile.id + ".",
										autoSelectSingleRecord: !searchClicked,
										filter: searchClicked?undefined:[{field: field??"", value: value, operator: "startsWith"}]
									});
								}}
							/>
						</CardContent>
					</Card>}
				{attachments && attachments.length > 0 && <>
					<Divider textAlign={"left"}><Typography variant="h6">Attachments</Typography></Divider>
					{attachments.map((it) => {
						return (
							<Card key={it.id} sx={{mb: 1}} variant="outlined" title={it.name} lang={it.name}>
								<CardHeader title={
									<Typography noWrap gutterBottom variant="h6" component="h4">
										{it.name}
									</Typography>
								} avatar={
									<Button variant={"outlined"} onClick={() => {
										props.setCurrentFile && props.setCurrentFile(it.id);
									}
									}>
										<Avatar sx={{ bgcolor: blue[(it.id ===props.currentFile) ? 500 : 50] }}>
											<AttachFile />
										</Avatar>
									</Button>
								}/>
								<CardContent>
									<MetaSelector
										key={filesProps.metaKeyPrefix + it.id + ".Ignore"}
										readOnly={props.readOnly}
										metaType={MetaValueType.Boolean}
										label={translate("Ignore File")}
										metaKey={filesProps.metaKeyPrefix + it.id + ".Ignore"}
										search={false}
										metaData={metaData}
										validations={validations}
										onChange={async (key, field, value) => await updateField({
											key: key,
											value: value
										})}
									/>
									{!metaObject.getMetaValueBoolean(filesProps.metaKeyPrefix + it.id + ".Ignore") &&
										<>
											<MetaSelector
												key={filesProps.metaKeyPrefix + it.id + ".Type"}
												field={"Description"}
												label={translate("Type")}
												metaKey={filesProps.metaKeyPrefix + it.id + ".Type"}
												metaType={MetaValueType.String}
												search={true}
												metaData={metaData}
												validations={validations}
												onSearch={(field, value, searchClicked) => {
													setLookup({
														type: filesProps.fileTypeLookup,
														metaKeyPrefix: filesProps.metaKeyPrefix + it.id + ".",
														autoSelectSingleRecord: !searchClicked,
														filter: searchClicked?undefined:[{field: "description", value: value, operator: "startsWith"}]
													});
												}}
												endAdornment={pasteFileType && <Tooltip title={"Paste " + pasteFileType.fileType}><IconButton size="small" onClick={async () => {
													await updateFields([{key:filesProps.metaKeyPrefix + it.id + ".TypeId", value: pasteFileType.fileTypeId}, {key:filesProps.metaKeyPrefix + it.id + ".Type", value: pasteFileType.fileType}] );
												}}><ContentPaste></ContentPaste>{pasteFileType.fileTypeId}</IconButton></Tooltip>}
											/>
											{filesProps.attachmentExtraInput?.(it)}
										</>
									}
								</CardContent>
							</Card>
						);
					})}
				</>}
			</>
		);
	};

	const SurebaseInput = () => {
		const hiddenFields = metaData?.filter((it) => it.key.startsWith(SUREBASE_HIDDEN_FIELD_PREFIX)) ?? [];

		return (
			<Stack key={"surebase" + metaUpdate}>
				<Divider textAlign={"left"}><Typography variant="h6">SureBase</Typography></Divider>
				<MetaSelector
					key="surebaseCategory"
					readOnly={props.readOnly}
					field={"description"}
					label={translate("Category")}
					metaKey={"surebaseCategory"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked)=> {
						setLookup({type: lookupType.SurebaseCategory,autoSelectSingleRecord: !searchClicked});
					}}
					onChange={async (key, field, value) => {
						setLookup({type: lookupType.SurebaseCategory,autoSelectSingleRecord: false, filter: [{field: field??"", value: value, operator: "startsWith"}]});
						return true;
					}}
				/>
				{hiddenFields.length > 0 && <>
					<Divider textAlign={"left"}><Typography variant="h6">{translate("Hidden fields")}</Typography></Divider>
					{hiddenFields.map((it) =>
						<MetaSelector key={it.key} readOnly={true}
							label={it.key.substring(SUREBASE_HIDDEN_FIELD_PREFIX.length)} metaKey={it.key} metaType={MetaValueType.String} search={false} metaData={metaData} validations={validations}/>
					)}
				</>}
				{FilesInput({
					metaKeyPrefix: "SurebaseFile.",
					fileTypeLookup: lookupType.SurebaseFileType
				})}
			</Stack>
		);
	};

	const CedInput = () => {
		const bodyFile = props.files?.filter((it) => it.type === DocumentFileType.Body).first();
		
		if (bodyFile && !metaObject.getMetaValue("CedFile." + bodyFile.id + ".TypeId")) {
			updateFields([
				{key: "CedFile." + bodyFile.id + ".TypeId", value: "COR"},
				{key: "CedFile." + bodyFile.id + ".Type", value: "Correspondentie partijen"}
			]);
		}
		
		return (
			<>
				<MetaSelector
					readOnly={props.readOnly}
					key="claim2"
					field={"claim.cedSchadenr"}
					label={"CED nr"}
					metaKey={"claimCedNr"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked) => {
						setLookup({
							type: lookupType.Claim,
							autoSelectSingleRecord: !searchClicked,
							filter: [{field: field, value: value, operator: "equals"}]
						});
					}}
				/>
				
				<MetaSelector
					key={"claimCedConcerns"}
					field={"description"}
					label={translate("Concerns")}
					metaKey={"claimCedConcerns"}
					metaType={MetaValueType.String}
					search={true}
					metaData={metaData}
					validations={validations}
					onSearch={(field, value, searchClicked) => {
						setLookup({
							type: lookupType.ClaimCedConcerns,
							autoSelectSingleRecord: !searchClicked,
							filter: searchClicked?undefined:[{field: field??"", value: value, operator: "startsWith"}]
						});
					}}
					/*
					onChange={async (key, field, value) => await updateField({
						key: key,
						value: value
					})}
*/
				/>
				
				{FilesInput({
					metaKeyPrefix: "CedFile.",
					fileTypeLookup: lookupType.CedFileType,
					attachmentExtraInput: (file) => metaObject.getMetaValue("CedFile." + file.id + ".TypeId") === "SAF" &&
						<MetaSelector
							key={"CedFile." + file.id + ".Signed"}
							readOnly={props.readOnly}
							metaType={MetaValueType.RadioButtons}
							label={translate("Signing")}
							metaKey={"CedFile." + file.id + ".Signed"}
							search={false}
							metaData={metaData}
							validations={validations}
							onChange={async (key, field, value) => await updateField({
								key: key,
								value: value
							})}
							values={[{value:translate("Signed"), key:"true"},{value:translate("Not signed"), key:"false"}]}
						/>
				})}
			</>
		);
	};
	
	const TargetInput = () => {
		
		return(
			<>
				{ metaObject.target === "CED" &&
					<>
						{/*
						<MetaSelector
							search={false}
							readOnly={props.readOnly}
							key={"targetFileName" + metaUpdate}
							label={translate("CED Filename (without extension)")}
							metaKey={"targetFileName"}
							metaType={MetaValueType.String}
							metaData={metaData}
							validations={validations}
							onChange={async (key, field, value) => await updateField({key: key, value: value})}
						/>
*/}
						<CedInput/>
					</>
				}
				{ metaObject.target === "SUREBASE" && SurebaseInput() }
				{ metaObject.target === "FINANCE" &&
						<MetaSelector
							search={false}
							readOnly={props.readOnly}
							key={"targetFileName" + metaUpdate}
							label={translate("Filename (without extension)")}
							metaKey={"targetFileName"}
							metaType={MetaValueType.String}
							metaData={metaData}
							validations={validations}
							onChange={async (key, field, value) => await updateField({key: key, value: value})}
						/>
				}

			</>
		);
		
	};
	
	const ClaimInput = () => {

		return(

			<Stack key={"claim" + metaUpdate}>
				<Divider textAlign={"left"}>
					<Typography variant="h6">
						Schade
						{metaObject.claimInjury && <Tooltip title={translate("Injury")}><IconButton><LocalHospital color="error"/></IconButton></Tooltip>}
						{!props.readOnly && <IconButton onClick={async () => await updateFields([{key:"claimId", value:""},{key:"cedSchadenr", value:""},{key:"newClaim", value:""},{key:"claimNr", value:""},{key:"claimDate", value:""},{key:"claimType", value:""}])}><Delete/></IconButton>}
					</Typography>
				</Divider>

				<MetaSelector
					readOnly={props.readOnly}
					key = "newClaim"
					metaType = {MetaValueType.Boolean}
					label={translate("New claim")}
					metaKey={"newClaim"}
					search={false}
					metaData={metaData}
					validations={validations}
					onChange={async (key, field, value) => await updateField({key: key, value: value})}
				/>

				{metaObject.newClaim &&
					<>
						<MetaSelector
							readOnly={props.readOnly}
							key = "claimInjury"
							metaType = {MetaValueType.Boolean}
							label={translate("Injury")}
							metaKey={"claimInjury"}
							search={false}
							metaData={metaData}
							validations={validations}
							onChange={async (key, field, value) => await updateField({key: key, value: value})}
						/>
						<MetaSelector
							readOnly={props.readOnly}
							disableFuture
							key = "claimDate"
							metaType = {MetaValueType.Date}
							label={translate("Damage date")}
							metaKey={"claimDate"}
							search={false}
							metaData={metaData}
							validations={validations}
							onChange={async (key, field, value) => await updateField({key: key, value: value})}
						/>
						<MetaSelector
							key="claimType"
							field={"omschrijving"}
							label={translate("Claimtype")}
							metaKey={"claimType"}
							metaType={MetaValueType.String}
							search={true}
							metaData={metaData}
							validations={validations}
							onSearch={(field, value, searchClicked)=> {
								setLookup({type: lookupType.ClaimType,autoSelectSingleRecord: !searchClicked});
							}}
							//onChange={async (key, field, value) => await updateField({key: key, value: value})}
						/>
					</>

				}

				{!metaObject.newClaim &&
					<Stack>
						<MetaSelector key = "claimId" hideInput={false} readOnly={true} hideIfEmpty={true}
							field={"claim.id"} label={"Id"} metaKey={"claimId"} metaType={MetaValueType.String} search={false} metaData={metaData} validations={validations}/>
						<MetaSelector
							readOnly={props.readOnly}
							key="claim1"
							field={"claim.schadenr"}
							label={"Nr"}
							metaKey={"claimNr"}
							metaType={MetaValueType.String}
							search={true}
							metaData={metaData}
							validations={validations}
							onSearch={(field, value, searchClicked)=> {
								let filter: GridFilterItem[] = [{field: "claim.schadenr", value:parseInt(value??"0"), operator: "equals"}];
								const policyId = metaData?.filter((it)=> it.key === "policyId" ).first()?.value;
								if (policyId) {
									filter = [...filter, {field: "policy.policyId", value: policyId, operator: "equals"}];
								}
								if (metaObject.customerId) {
									filter = [...filter, {field: "policy.relnr", value: metaObject.customerId, operator: "equals"}];
								}

								setLookup({type: lookupType.Claim,autoSelectSingleRecord: !searchClicked, filter: filter});
							}}
							onChange={async () => false}
						/>
						<MetaSelector
							readOnly={props.readOnly}
							key="claim2"
							field={"claim.cedSchadenr"}
							label={"CED nr"}
							metaKey={"claimCedNr"}
							metaType={MetaValueType.String}
							search={true}
							metaData={metaData}
							validations={validations}
							onSearch={(field, value, searchClicked)=> {
								setLookup({type: lookupType.Claim,autoSelectSingleRecord: !searchClicked, filter: [{field: field, value: value, operator: "equals"}]});
							}}
						/>
					</Stack>
				}
			</Stack>
		);
	};

	const updateField = async (item: DocumentMeta) : Promise<boolean> => {
		return updateFields([item]);
	};
	const updateFields = async (items: DocumentMeta[], skipValidations?: boolean, autoSave?: boolean ): Promise<boolean> => {
		if (!metaData) {
			return false;
		}

		let updatedMetaData = [...metaData];
		items.map((newItem) => {
			const meta = metaData.filter((it)=> it.key === newItem.key).first();

			newItem.changed = true;
			newItem.selected = true;
			newItem.timeStamp??= new Date().toISOString();
			newItem.valueType??=MetaValueType.String;
			newItem.confidence??=100;
			newItem.documentId = props.documentId;
			newItem.id = meta?.id??0;

			if (meta && meta.value === newItem.value && meta.confidence === newItem.confidence) {
				return false;
			}

			updatedMetaData = [...updatedMetaData.filter((it) => it.key !== newItem.key), newItem];
			return true;
		});
		if (skipValidations) {
			updateMetaData(updatedMetaData);
			return true;
		}
		setLoading(true);
		const result = await graphQLServices.documentsClient.query({
			query: gqlOnChangeMeta,
			variables: {
				input: {
					documentId: props.documentId,
					metaData: updatedMetaData,
					autoSave: autoSave??false
				}
			},
		});
		updateMetaData(result.data.onChangeMeta.onChangeMeta.metaData);
		updateValidations(result.data.onChangeMeta.onChangeMeta.validations);
		updateAudits(result.data.onChangeMeta.onChangeMeta.audits);
		setLoading(false);
		return result.data.onChangeMeta.onChangeMeta.updated as boolean;

	};
	const getMetaInputField = (meta: DocumentMeta) => {
		const validations = props.validations?.filter(
			(it) => it.field === meta.key
		);

		let inputStyle: React.CSSProperties = {};
		if (validations && validations.length > 0) {
			inputStyle = { borderColor: "red" };
		}

		return (
			<>
				{validations && validations.length > 0 && (
					<Tooltip
						title={
							<>
								{validations?.map((it) => {
									return (
										<div key={it.id}>
											<Typography color="inherit">
												{ValidationSeverity[it.severity].toString()}
											</Typography>
											{it.message}
										</div>
									);
								})}
							</>
						}
					>
						{validations.findIndex(
							(it) => it.severity === ValidationSeverity.ERROR
						) > -1 ? (
								<ErrorOutline color="error" />
							) : (
								<WarningOutlined color="warning" />
							)}
					</Tooltip>
				)}
				<Input
					key={new Date().getMilliseconds()}
					style={inputStyle}
					readOnly
					id={"meta-edit-" + meta.id}
					// TODO onBlur={(event) => updateMetaData(meta.id, event.target.value)}
					defaultValue={meta.value}
				/>
				{meta.source}
			</>
		);
	};

	const selectCustomer = async (row: any) => {
		await updateField({key:"customerId", value:row.customer.id});
		setLookup(undefined);
	};

	const selectPolicy = async (row: any) => {
		await updateField({key:"policyId", value:row.policy.id});
		setLookup(undefined);
	};

	const selectClaim = async (row: any) => {
		await updateField({key:"claimId", value:row.claim.id});
		setLookup(undefined);
	};

	const selectDocumentCategory = async (row: any) => {
		await updateFields([
			{key:"documentCategory", value:row.description},
			{key:"documentCategoryId", value:row.id.toString()},
		]);
		setLookup(undefined);
	};

	const selectSurebaseCategory = async (row: any) => {
		await updateFields([
			{key:"surebaseCategory", value:row.description},
			{key:"surebaseCategoryId", value:row.code},
		]);
		setLookup(undefined);
	};

	const selectClaimType = async (row: any) => {
		await updateFields([
			{key:"claimType", value:row.omschrijving},
			{key:"claimTypeId", value:row.key},
		]);
		setLookup(undefined);
	};

	const selectTaskType = async (row: any) => {
		await updateFields([
			{key:"taskType", value:row.description},
			{key:"taskTypeId", value:row.code},
		]);
		setLookup(undefined);
	};
	
	const selectFileType = async (row: any, keyPrefix:string, fileTypeLookup: lookupType) => {
		await updateFields([
			{key:keyPrefix+"Type", value:row.description},
			{key:keyPrefix+"TypeId", value:row.code},
		]);
		setCopyFileType({fileTypeId:row.code, fileType:row.description, lookup: fileTypeLookup });
		setLookup(undefined);
	};
	
	const selectClaimCedConcerns = async (row: any) => {
		await updateFields([
			{key:"claimCedConcerns", value:row.description},
			{key:"claimCedConcernsId", value:row.code},
		]);
		setLookup(undefined);
	};
	
	const selectDocumentSegment = async (row: any) => {
		await updateFields([
			{key:"documentSegment", value:row.omschrijving},
			{key:"documentSegmentId", value:row.key},
		]);
		setLookup(undefined);
	};
	const selectUser = async (row: any) => {

		await updateFields([
			{key: "assignedUser", value:row.description},
			{key: "assignedUserId", value:row.code},
			{key: "assignedUserOverride", value:"true"},
		]);
		setLookup(undefined);
	};


	return (
		<>
			<ModalLoader open={loading} transparancy={0}></ModalLoader>
			<Card>



				{   lookup?.type === lookupType.Segment && <GenericModal open title={"Segment Lookup"} onClose={() => setLookup(undefined)  }>
					<RefBesLookup labelId={"98509"} activeOnly={true} defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectDocumentSegment(row)}/>
				</GenericModal>
				}


				{   lookup?.type === lookupType.Customer && <GenericModal open title={"Customer Lookup"} onClose={() => setLookup(undefined)  }>
					<CustomerLookup defaultFilter={lookup.filter} onRowClick={(row) => selectCustomer(row)} autoSelectSingleRecord={lookup.autoSelectSingleRecord}/>
				</GenericModal>
				}
				{   lookup?.type === lookupType.Policy && <GenericModal open title={"Policy Lookup"} onClose={() => setLookup(undefined)  }>
					<PolicyLookup defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectPolicy(row)}/>
				</GenericModal>
				}
				{   lookup?.type === lookupType.Claim && <GenericModal open title={"Claim Lookup"} onClose={() => setLookup(undefined)  }>
					<ClaimLookup defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectClaim(row)}/>
				</GenericModal>
				}
				{ lookup?.type === lookupType.Category && <GenericModal open title={"Category Lookup"} onClose={() => setLookup(undefined) }>
					<Lookup type={"CATEGORY"} hideCode={true} defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectDocumentCategory(row)}/>
				</GenericModal>
				}
				{   lookup?.type === lookupType.ClaimType && <GenericModal open title={"Claim Lookup"} onClose={() =>  setLookup(undefined)  }>
					<RefBesLookup labelId={"90004"} activeOnly={true} defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectClaimType(row)}/>
				</GenericModal>
				}

				{ lookup?.type === lookupType.User && <GenericModal open title={"User Lookup"} onClose={() => setLookup(undefined)  }>
					<Lookup type={"USER"} defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectUser(row)}/>
				</GenericModal>
				}

				{   lookup?.type === lookupType.TaskType && <GenericModal open title={"Task type Lookup"} onClose={() =>  setLookup(undefined) }>
					<Lookup  type={"TASKTYPE"}  defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectTaskType(row)}/>
				</GenericModal>
				}
				{   lookup?.type === lookupType.CedFileType && <GenericModal open title={"File type Lookup"} onClose={() =>  setLookup(undefined) }>
					<Lookup  type={"CEDFILETYPE"}  defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectFileType(row,lookup.metaKeyPrefix??"", lookupType.CedFileType)}/>
				</GenericModal>
				}
				{ lookup?.type === lookupType.SurebaseCategory && <GenericModal open title={"Category Lookup"} onClose={() => setLookup(undefined) }>
					<Lookup type={"SUREBASECATEGORY"} hideCode={true} defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectSurebaseCategory(row)}/>
				</GenericModal>
				}
				{   lookup?.type === lookupType.SurebaseFileType && <GenericModal open title={"File type Lookup"} onClose={() =>  setLookup(undefined) }>
					<Lookup  type={"SUREBASEFILETYPE"} hideCode={true} defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectFileType(row,lookup.metaKeyPrefix??"", lookupType.SurebaseFileType)}/>
				</GenericModal>
				}
				{   lookup?.type === lookupType.ClaimCedConcerns && <GenericModal open title={"Concerns Lookup"} onClose={() =>  setLookup(undefined) }>
					<Lookup  type={"CEDCONCERNS"}  defaultFilter={lookup.filter} autoSelectSingleRecord={lookup.autoSelectSingleRecord} onRowClick={(row) => selectClaimCedConcerns(row)}/>
				</GenericModal>
				}

				
				<CardContent>
					{validations && validations.filter((it) => !it.field).length > 0 && (
						<Stack>
							{validations.filter((it) => !it.field).map((it) => {
								return (
									<>
										<Alert severity={it.severity === ValidationSeverity.ERROR ? "error": "warning"}>{it.message}</Alert>
									</>
								);
							})}
						</Stack>
					)}
					<Stack direction={"row"}>
						{validations && validations.length > 0 && (
							<Stack>
								{validations?.filter(it=> it.field === "target").map((it) => {
									return (
										<Alert key={it.id} severity={it.severity === ValidationSeverity.ERROR ? "error": "warning"}>{translate(it.message)}</Alert>
									);
								})}
							</Stack>
						)}
						{!props.hideFields?.includes("target") &&
						<RadioGroup
							row
							aria-labelledby="radio-buttons-group-label"
							name="radio-buttons-group"
							aria-readonly={props.readOnly}
							value={metaObject.target??""}
							onChange={async (event) => {
								await updateField({key:"target", value: event.target.value});
							}}
						>
							{ targets && targets.length > 1 &&
								targets.map((target) => {
									return (
										<FormControlLabel disabled={props.readOnly} key={target.id} value={target.code} control={<Radio/>} label={target.description}/>
									);
								})
							}
						</RadioGroup>
						}
					</Stack>
					{(!metaObject.target ||metaObject.target === "ANVA") &&
					<Stack>
						{GeneralInput()}
						{CustomerInput()}
						{PolicyInput()}
						{ClaimInput()}
						{TaskInput()}
					</Stack>
					}
					<Stack>
						{TargetInput()}
					</Stack>
					{isAuthorizedByAnyGroup(["Admin"]) &&
					<Stack>
						<FormControlLabel control={
							<Switch name="Debug" checked={debug} onChange={() => setDebug(!debug)} />
						} label="Debug MetaData"/>
						{debug && <>
							<Divider textAlign={"left"}>MetaData</Divider>
							<div style={{ overflow: "scroll" }}>
								{
									metaData
										?.map((meta, index) => (
											<div key={index}>
												<Typography variant="body1" color="text.primary">
													{meta.key}
												</Typography>
												<div
													style={{
														display: "flex",
														flexDirection: "row",
														alignItems: "center",
													}}
												>
													{getMetaInputField(meta)}
												</div>
												<hr/>
											</div>
										))}
							</div>
						</> }
					</Stack>}
				</CardContent>
			</Card>
		</>
	);
};
